import React, { useState, useCallback, useRef, useMemo } from 'react'
import { Message } from 'rsuite'
import useStandardToaster from '../../ui_reusable/StandardToaster'
import VersatileGrid from '../../ui_reusable/VersatileGrid'
import {
  GridCellBoldText,
  GridCellNormalText,
  GridCellSmallFadedText,
} from '../../ui_reusable/styles/VersatileGridUtils.styles'
import { EasyIconButton } from '../../ui_reusable/styles/Button.styles'
import CustomIcons from '../../ui_reusable/CustomIcons'
import theme from '../../../styles/theme'
import { StatsContainer } from '../../ui_reusable/styles/VersatileGridUtils.styles'
import { HeaderMedium600Style } from '../../../styles/Text.styles'
import { SmallNormalTag } from '../../ui_reusable/styles/Tags.styles'
import { StandardCheckbox } from '../../ui_reusable/StandardInputs'
import { HeaderCellFont } from '../../ui_reusable/styles/VersatileGrid.styles'
import ShowDatasetDropdown from '../datasets/ShowDatasetDropdown'
import { getQualityTagByQualityString } from '../../../utils/ScoreFitnessValidation'
import MarketIcon from '../../ui_reusable/MarketIcon'
import DriverSelectionFilter from './DriverSelectionFilter'

const SelectAllCheckbox = React.memo(
  React.forwardRef(({ allSelected, onSelectAll, disabled = false }, ref) => {
    return (
      <div
        ref={ref}
        style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
      >
        <StandardCheckbox
          onChange={onSelectAll}
          checked={allSelected}
          id="select-all-checkbox"
          disabled={disabled}
        />
        <span css={HeaderCellFont}>Select all/none</span>
      </div>
    )
  }),
)

const SelectableDatasetList = ({
  clientId,
  data,
  onToggleDriver,
  onToggleMultipleDrivers,
  loading,
  setLoading,
  onRefresh,
  // Opt-in server-side mode (backward compatible — unset = current behavior).
  // In server mode `data` is the current page; sortable/searchable columns are
  // restricted to `serverSortableKeys`/`serverSearchableKeys` (backend support
  // differs per endpoint), and the search box can be hidden entirely via
  // `hideSearchInServerMode` when the endpoint cannot filter at all.
  serverSide = false,
  serverTotal,
  serverSelectedTotal,
  serverPage = 1,
  onPageChange,
  onSearchChange,
  onSortChange,
  serverSortableKeys = [],
  serverSearchableKeys = [],
  hideSearchInServerMode = false,
  serverSelected,
  /**
   * When set (server-side), select-all/none is owned by the parent so it can
   * cover every matching driver across pages, not only the current page.
   */
  onSelectAllToggle,
  selectionDisabled = false,
  showSelectedOnly: showSelectedOnlyProp,
  onShowSelectedChange,
}) => {
  const filteredDriverIdsRef = useRef(new Set())
  const selectAllCheckboxRef = useRef(null)
  const { pushToast } = useStandardToaster()
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [internalShowSelectedOnly, setInternalShowSelectedOnly] =
    useState(false)
  const isSelectedFilterControlled = typeof onShowSelectedChange === 'function'
  const showSelectedOnly = isSelectedFilterControlled
    ? Boolean(showSelectedOnlyProp)
    : internalShowSelectedOnly

  const handleShowSelectedChange = useCallback(
    (next) => {
      if (isSelectedFilterControlled) {
        onShowSelectedChange(next)
      } else {
        setInternalShowSelectedOnly(next)
      }
    },
    [isSelectedFilterControlled, onShowSelectedChange],
  )

  const flattenedData = useMemo(() => {
    const rows = data.map((item) => ({
      ...item.dataset,
      is_active: item.is_active,
      _originalDataset: item.dataset,
    }))
    if (!showSelectedOnly) return rows
    return rows.filter((item) => item.is_active)
  }, [data, showSelectedOnly])

  const getFilteredDrivers = useCallback(() => {
    if (filteredDriverIdsRef.current.size === 0) return data

    return data.filter((driver) =>
      filteredDriverIdsRef.current.has(driver.dataset.id),
    )
  }, [data])

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      filteredDriverIdsRef.current = new Set(
        filteredData.map((item) => item.id),
      )

      const filteredDrivers = data.filter((driver) =>
        filteredDriverIdsRef.current.has(driver.dataset.id),
      )
      const newAllFilteredSelected =
        filteredDrivers.length > 0 &&
        filteredDrivers.every((driver) => driver.is_active)

      if (selectAllCheckboxRef.current) {
        const checkbox = selectAllCheckboxRef.current.querySelector(
          'input[type="checkbox"]',
        )

        if (checkbox) {
          checkbox.checked = newAllFilteredSelected
        }
      }
    },
    [data],
  )

  const handleDriverToggle = async (driverId) => {
    if (selectionDisabled) return

    const driver = data.find((driver) => driver.dataset.id === driverId)

    if (!driver) return

    try {
      await onToggleDriver(driverId, !driver.is_active)
      pushToast(<Message type="info">Driver selection updated.</Message>, {
        placement: 'topCenter',
        duration: 5000,
      })
    } catch (error) {
      pushToast(
        <Message type="error">
          {error.message || 'Failed to update driver selection'}
        </Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  const handleSelectAll = useCallback(async () => {
    if (selectionDisabled) return

    setLoading(true)
    try {
      if (onSelectAllToggle) {
        const targetState = await onSelectAllToggle()
        pushToast(
          <Message type="info">
            {targetState
              ? 'All matching drivers selected.'
              : 'All matching drivers deselected.'}
          </Message>,
          { placement: 'topCenter', duration: 5000 },
        )
        return
      }

      const filteredDrivers = getFilteredDrivers()
      const driversToUpdate =
        filteredDrivers.length > 0 ? filteredDrivers : data
      const allSelected = driversToUpdate.every(
        (driver) => driver.is_active === true,
      )
      const targetState = !allSelected

      await onToggleMultipleDrivers(driversToUpdate, targetState)
      pushToast(
        <Message type="info">
          {targetState
            ? 'All visible drivers selected.'
            : 'All visible drivers deselected.'}
        </Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    } catch (error) {
      console.error('Failed to update drivers:', error)
      pushToast(
        <Message type="error">Failed to update driver selection.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      if (onRefresh) onRefresh()
    } finally {
      setLoading(false)
    }
  }, [
    data,
    getFilteredDrivers,
    onToggleMultipleDrivers,
    onSelectAllToggle,
    pushToast,
    setLoading,
    onRefresh,
    selectionDisabled,
  ])

  const allFilteredSelected = onSelectAllToggle
    ? Boolean(
        serverTotal > 0 &&
          serverSelected > 0 &&
          serverSelected === serverTotal,
      )
    : (() => {
        const filteredDrivers = getFilteredDrivers()

        return (
          filteredDrivers.length > 0 &&
          filteredDrivers.every((driver) => driver.is_active)
        )
      })()

  const handleExpand = useCallback((dataIdx) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)

      if (next.has(dataIdx)) {
        next.delete(dataIdx)
      } else {
        next.add(dataIdx)
      }

      return next
    })
  }, [])

  const headers = useMemo(() => {
    const base = [
      {
        title: 'Driver Name',
        key: 'name',
        type: 'string',
        isSortable: false,
        isSearchable: true,
        flexGrow: 2,
        overrideJSX: (
          <SelectAllCheckbox
            ref={selectAllCheckboxRef}
            allSelected={allFilteredSelected}
            onSelectAll={handleSelectAll}
            disabled={selectionDisabled}
          />
        ),
      },
      {
        title: 'Source',
        key: 'source',
        type: 'string',
        isSortable: true,
        isSearchable: true,
      },
      {
        title: 'Country',
        key: 'markets',
        type: 'string',
        isSortable: true,
        isSearchable: true,
        center: true,
        cellContentStyle: { overflow: 'visible' },
        getSortValue: (row) => row.markets?.map((m) => m.name).join(', ') || '',
      },
      {
        title: 'Last Updated',
        key: 'updated_at',
        type: 'datetime',
        isSortable: true,
        isSearchable: true,
        serverKey: 'updated_at',
      },
      {
        title: 'Observations',
        key: 'total_observations',
        type: 'number',
        isSortable: true,
        isSearchable: true,
        center: true,
      },
      {
        title: 'Quality',
        key: 'quality',
        type: 'string',
        isSortable: true,
        isSearchable: true,
        center: true,
        serverKey: 'quality',
      },
      {
        title: 'View',
        key: 'actions',
        type: 'none',
        isSortable: false,
        isSearchable: false,
        center: true,
      },
    ]

    if (!serverSide) return base
    // Server mode: only columns the endpoint supports stay interactive.
    // (`serverKey` carries the backend ordering field where one exists.)
    const sortable = new Set(serverSortableKeys)
    const searchable = new Set(serverSearchableKeys)
    return base.map((h) => ({
      ...h,
      isSortable: sortable.has(h.key),
      isSearchable: searchable.has(h.key),
    }))
  }, [
    allFilteredSelected,
    handleSelectAll,
    serverSide,
    serverSortableKeys,
    serverSearchableKeys,
    selectionDisabled,
  ])

  const availableCount = serverSide
    ? (serverTotal ?? data.length)
    : data.length
  const selectedCount = serverSide
    ? (serverSelected ?? data.filter((e) => e.is_active).length)
    : data.filter((e) => e.is_active).length

  const gridHeader = (
    <StatsContainer>
      <span css={HeaderMedium600Style}>
        Available Drivers: {availableCount}
      </span>
      <div>
        <SmallNormalTag>{selectedCount} drivers selected</SmallNormalTag>
      </div>
      <DriverSelectionFilter
        showSelectedOnly={showSelectedOnly}
        onChange={handleShowSelectedChange}
      />
    </StatsContainer>
  )

  const renderIntersectingRow = useCallback(
    (rowData, rowIdx, dataIdx) => {
      if (!expandedRows.has(dataIdx)) return null

      return (
        <ShowDatasetDropdown
          dataset={rowData._originalDataset}
          datasetId={rowData.id}
          units={rowData.units}
        />
      )
    },
    [expandedRows],
  )

  return (
    <VersatileGrid
      headers={headers}
      data={flattenedData}
      searchPlaceholder="Find driver"
      isSearchable={!(serverSide && hideSearchInServerMode)}
      serverSide={serverSide}
      serverTotal={serverTotal}
      serverPage={serverPage}
      onPageChange={onPageChange}
      onSearchChange={onSearchChange}
      onSortChange={onSortChange}
      renderRow={(rowData, rowIdx, dataIdx) => [
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <StandardCheckbox
            onChange={() => handleDriverToggle(rowData.id)}
            checked={rowData.is_active}
            disabled={selectionDisabled}
          />
          <GridCellBoldText>{rowData.name}</GridCellBoldText>
        </div>,
        <GridCellNormalText>{rowData.source}</GridCellNormalText>,
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <MarketIcon markets={rowData.markets} />
        </div>,
        <GridCellSmallFadedText>
          {new Date(rowData.updated_at).toISOString().slice(0, 10)}
        </GridCellSmallFadedText>,
        <GridCellNormalText>{rowData.total_observations}</GridCellNormalText>,
        <GridCellNormalText>
          {getQualityTagByQualityString(rowData.quality)}
        </GridCellNormalText>,
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          <EasyIconButton onClick={() => handleExpand(dataIdx)}>
            {expandedRows.has(dataIdx) ? (
              <CustomIcons.faEyeSlash
                color={theme.colors.veryFadedText}
                solid={false}
                size="xs"
              />
            ) : (
              <CustomIcons.faEye
                color={theme.colors.veryFadedText}
                solid={false}
                size="xs"
              />
            )}
          </EasyIconButton>
        </div>,
      ]}
      renderIntersectingRow={renderIntersectingRow}
      maxDisplayedItems={8}
      headerInfo={gridHeader}
      loading={loading}
      loadingMessage={'Loading datasets...'}
      onFilteredDataChange={handleFilteredDataChange}
    />
  )
}

export default SelectableDatasetList
